import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useTheme } from "@mui/material/styles";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useIntl } from "../../contexts/IntlContext";
import LoadingButton from "../../components/ui/LoadingButton";
import { AuthContext } from "../../contexts/AuthContext";
import { useCountry } from "../../contexts/CountryContext";
import {
  hasOperationalAccess,
  isAuthAdmin,
  isAuthOwner,
} from "../../utils/authRole";
import api from "../../services/api";
import { fetchEstablishmentsForFilters } from "../../services/establishmentsResolver";
import { useYoutubeOAuthPopup } from "../../hooks/useYoutubeOAuthPopup";
import MediaManager from "./components/MediaManager";
import PlaylistsManager from "./components/PlaylistsManager";
import LivesManager from "./components/LivesManager";
import { OWNER_YOUTUBE_CONNECTION_REQUIRED } from "../../constants/ownerYoutubePolicy";
import OwnerYoutubeConnection from "./components/OwnerYoutubeConnection";
import LiveCreationDialog from "./components/LiveCreationDialog";
import BillingConfigPanel from "./components/LivePeriod/BillingConfigPanel";
import {
  YoutubeOwnerConnectionStatus,
  buildSupportWhatsAppUrl,
  clearYoutubeReconnectNotification,
  disconnectYoutubeOwnerConnection,
  getYoutubeOwnerConnectionStatus,
  pushYoutubeNotification,
  startYoutubeOwnerConnection,
  takePendingYoutubeOAuthPopup,
  upsertYoutubeReconnectNotification,
} from "../../services/youtubeOwnerConnection";
import {
  CameraStatusRow,
  EstablishmentWithChannels,
  LivePlaylistOption,
} from "./types";

export default function LiveStreams() {
  const intl = useIntl();
  const { country } = useCountry();
  const theme = useTheme();
  const isLocalhost = useMemo(
    () => ["localhost", "127.0.0.1"].includes(String(window.location.hostname)),
    [],
  );
  const location = useLocation();
  const { authInfo } = useContext(AuthContext);
  const canOperate = hasOperationalAccess(authInfo?.role);
  const isOwner = isAuthOwner(authInfo?.role);
  const isAdmin = isAuthAdmin(authInfo?.role);
  const isOwnerOnly = isOwner && !canOperate;
  const playlistListContextLiveId = useMemo(() => {
    const raw = Number(
      (location.state as { focusLiveId?: number | null } | null)?.focusLiveId,
    );
    return canOperate && Number.isFinite(raw) && raw > 0 ? raw : null;
  }, [canOperate, location.state]);

  const [establishments, setEstablishments] = useState<
    EstablishmentWithChannels[]
  >([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] =
    useState<string>("");
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [initializingFilters, setInitializingFilters] = useState(true);
  const [establishmentsReady, setEstablishmentsReady] = useState(false);
  const initialCourtsResolvedRef = useRef(false);
  const [cameraCourts, setCameraCourts] = useState<CameraStatusRow[]>([]);
  const cameraCourtsCacheRef = useRef<Map<string, CameraStatusRow[]>>(
    new Map(),
  );
  const [livePlaylists, setLivePlaylists] = useState<LivePlaylistOption[]>([]);
  const [tab, setTab] = useState<"schedule" | "lives" | "playlists" | "media">(
    "lives",
  );
  const handleTabChange = (v: unknown) =>
    setTab(v as "schedule" | "lives" | "playlists" | "media");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [billingConfigOpen, setBillingConfigOpen] = useState(false);
  const [focusLiveId, setFocusLiveId] = useState<number | null>(null);
  const [youtubeConnectionStatus, setYoutubeConnectionStatus] =
    useState<YoutubeOwnerConnectionStatus | null>(null);
  const [youtubeStatusLoading, setYoutubeStatusLoading] = useState(false);
  const [youtubeDisconnectConfirmOpen, setYoutubeDisconnectConfirmOpen] =
    useState(false);
  const autoConnectYoutubeRef = useRef(false);

  useEffect(() => {
    if (canOperate) return;
    if (tab === "media") setTab("lives");
  }, [canOperate, tab]);

  const youtubeNotConnected = canOperate
    ? false
    : !youtubeConnectionStatus?.connected;
  const youtubeConnectionBlocked = OWNER_YOUTUBE_CONNECTION_REQUIRED && youtubeNotConnected;

  useEffect(() => {
    if (tab === "playlists" && youtubeNotConnected) setTab("lives");
  }, [tab, youtubeNotConnected]);

  useEffect(() => {
    if (canOperate) {
      setYoutubeConnectionStatus(null);
      setYoutubeStatusLoading(false);
      return;
    }
    let canceled = false;
    const load = async () => {
      setYoutubeStatusLoading(true);
      try {
        const status = await getYoutubeOwnerConnectionStatus();
        if (canceled) return;
        setYoutubeConnectionStatus(status);
        if (status.requiresReconnect) {
          upsertYoutubeReconnectNotification(
            intl.formatMessage({ id: "youtubeConnection.blockingWarning" }),
          );
        } else {
          clearYoutubeReconnectNotification();
        }
      } catch {
        if (!canceled) setYoutubeConnectionStatus(null);
      } finally {
        if (!canceled) setYoutubeStatusLoading(false);
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [intl, canOperate]);

  useEffect(() => {
    const navState = (location.state ?? {}) as {
      openTab?: string;
      focusLiveId?: number | null;
    };
    const requestedTab = String(navState.openTab ?? "").toLowerCase();
    if (requestedTab === "lives") {
      setTab("lives");
    } else if (requestedTab === "schedule") {
      setTab("lives");
      setScheduleDialogOpen(true);
    } else if (requestedTab === "playlists") {
      setTab("playlists");
    } else if (canOperate && requestedTab === "media") {
      setTab("media");
    }

    const rawFocus = Number(navState.focusLiveId);
    if (Number.isFinite(rawFocus) && rawFocus > 0) {
      setFocusLiveId(rawFocus);
    }
  }, [canOperate, location.state]);

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      if (!canceled) {
        setEstablishmentsReady(false);
        setInitializingFilters(true);
      }
      initialCourtsResolvedRef.current = false;
      try {
        const options = await fetchEstablishmentsForFilters({ country });
        const normalized: EstablishmentWithChannels[] = options.map(
          (option) => ({
            id: option.id,
            name: option.name,
            livePlaylistId: option.livePlaylistId,
            channels: [],
          }),
        );
        normalized.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        );
        if (!canceled) {
          setEstablishments(normalized);
          setEstablishmentsReady(true);
          if (normalized.length === 0) {
            toast.info(
              intl.formatMessage({ id: "liveStreams.noEstablishmentsFound" }),
            );
          }
        }
      } catch {
        try {
          const statusRes = await api.get<{ data: any[] }>(
            "/app/cameras/status",
          );
          const rows = Array.isArray((statusRes.data as any)?.data)
            ? (statusRes.data as any).data
            : [];
          const map = new Map<string, string>();
          for (const r of rows) {
            const id = r?.establishmentId ? String(r.establishmentId) : "";
            if (!id) continue;
            const name = r?.establishmentName
              ? String(r.establishmentName)
              : id;
            if (!map.has(id)) map.set(id, name);
          }
          const derived: EstablishmentWithChannels[] = Array.from(map.entries())
            .map(([id, name]) => ({
              id,
              name,
              livePlaylistId: null,
              channels: [],
            }))
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
            );
          if (!canceled) {
            setEstablishments(derived);
            setEstablishmentsReady(true);
            if (derived.length === 0) {
              toast.info(
                intl.formatMessage({ id: "liveStreams.noEstablishmentsFound" }),
              );
            }
          }
        } catch {
          if (!canceled) {
            setEstablishments([]);
            setEstablishmentsReady(true);
            toast.error(
              intl.formatMessage({ id: "liveStreams.establishmentsLoadError" }),
            );
          }
        }
      }
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [country, intl]);

  useEffect(() => {
    if (!establishments.length) return;
    const exists =
      !!selectedEstablishmentId &&
      establishments.some((e) => e.id === selectedEstablishmentId);
    if (!selectedEstablishmentId || !exists) {
      setSelectedEstablishmentId(establishments[0].id);
    }
  }, [establishments, selectedEstablishmentId]);

  const selectedEstablishment = useMemo(
    () => establishments.find((e) => e.id === selectedEstablishmentId) ?? null,
    [establishments, selectedEstablishmentId],
  );

  useEffect(() => {
    if (!selectedEstablishmentId) {
      setCameraCourts([]);
      return;
    }
    if (!establishmentsReady) return;
    // Wait until establishments list resolves the selected establishment.
    // This keeps establishment field as the first resolved filter and avoids
    // pre-filling court before the establishment autocomplete is ready.
    if (!selectedEstablishment) {
      return;
    }
    let canceled = false;
    const loadCourts = async () => {
      const cached = cameraCourtsCacheRef.current.get(selectedEstablishmentId);
      if (cached) {
        if (!canceled) {
          setCameraCourts(cached);
          if (!initialCourtsResolvedRef.current) {
            initialCourtsResolvedRef.current = true;
            setInitializingFilters(false);
          }
        }
        return;
      }
      setTemplatesLoading(true);
      try {
        const response = await api.get<{ data: CameraStatusRow[] }>(
          "/app/cameras/status",
          {
            params: { establishmentIds: selectedEstablishmentId },
          },
        );
        const rows = Array.isArray((response.data as any)?.data)
          ? (response.data as any).data
          : [];
        const normalized: CameraStatusRow[] = rows
          .map((r: any) => ({
            establishmentId: String(
              r.establishmentId ?? selectedEstablishmentId,
            ),
            establishmentName: r.establishmentName ?? null,
            deviceId: Number(r.deviceId),
            deviceName: String(r.deviceName ?? r.name ?? ""),
            channelUuid: r.channelUuid ? String(r.channelUuid) : undefined,
            channelName: r.channelName ? String(r.channelName) : undefined,
          }))
          .filter(
            (r: CameraStatusRow) =>
              r.establishmentId === selectedEstablishmentId,
          );
        normalized.sort((a, b) =>
          a.deviceName.localeCompare(b.deviceName, undefined, {
            sensitivity: "base",
          }),
        );
        cameraCourtsCacheRef.current.set(selectedEstablishmentId, normalized);
        if (!canceled) setCameraCourts(normalized);
      } catch {
        if (!canceled) setCameraCourts([]);
      } finally {
        if (!canceled) {
          setTemplatesLoading(false);
          if (!initialCourtsResolvedRef.current) {
            initialCourtsResolvedRef.current = true;
            setInitializingFilters(false);
          }
        }
      }
    };
    void loadCourts();
    return () => {
      canceled = true;
    };
  }, [establishmentsReady, selectedEstablishment, selectedEstablishmentId]);

  const youtubeSupportWhatsAppHref = useMemo(
    () =>
      buildSupportWhatsAppUrl(
        intl.formatMessage({ id: "youtubeConnection.supportWhatsappMessage" }),
      ),
    [intl],
  );

  const refreshYoutubeConnection = async () => {
    try {
      const status = await getYoutubeOwnerConnectionStatus();
      setYoutubeConnectionStatus(status);
      if (status.requiresReconnect) {
        upsertYoutubeReconnectNotification(
          intl.formatMessage({ id: "youtubeConnection.blockingWarning" }),
        );
      } else {
        clearYoutubeReconnectNotification();
      }
    } catch {
      setYoutubeConnectionStatus(null);
    }
  };

  const { connect: handleConnectYoutube, loading: youtubeConnectLoading } =
    useYoutubeOAuthPopup({
      start: popup => startYoutubeOwnerConnection({ popup }),
      getConnected: async () => (await getYoutubeOwnerConnectionStatus()).connected,
      onConnected: refreshYoutubeConnection,
    });
  const youtubeConnectionLoading = youtubeStatusLoading || youtubeConnectLoading;

  useEffect(() => {
    if (autoConnectYoutubeRef.current) return;
    if (canOperate || youtubeConnectionLoading) return;
    const navState = (location.state ?? {}) as { autoConnectYoutube?: boolean };
    if (!navState.autoConnectYoutube) return;
    if (youtubeConnectionStatus?.connected) return;
    autoConnectYoutubeRef.current = true;
    void handleConnectYoutube(takePendingYoutubeOAuthPopup());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOperate, location.state, youtubeConnectionLoading, youtubeConnectionStatus]);

  const handleDisconnectYoutube = async () => {
    setYoutubeDisconnectConfirmOpen(false);
    setYoutubeStatusLoading(true);
    try {
      await disconnectYoutubeOwnerConnection();
      await refreshYoutubeConnection();
      pushYoutubeNotification({
        message: intl.formatMessage({
          id: "youtubeConnection.disconnectNotice",
        }),
      });
      toast.success(
        intl.formatMessage({ id: "youtubeConnection.disconnectNotice" }),
      );
    } catch {
      toast.error(intl.formatMessage({ id: "global.generic_error" }));
    } finally {
      setYoutubeStatusLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Backdrop
        open={scheduleDialogOpen && (templatesLoading || initializingFilters)}
        sx={{
          color: "#fff",
          zIndex: theme.zIndex.modal + 30,
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {!canOperate ? (
        <OwnerYoutubeConnection
          status={youtubeConnectionStatus}
          loading={youtubeConnectionLoading}
          blockingReconnect={youtubeConnectionBlocked}
          supportWhatsAppHref={youtubeSupportWhatsAppHref}
          onConnect={handleConnectYoutube}
          onDisconnect={() => setYoutubeDisconnectConfirmOpen(true)}
        />
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => handleTabChange(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ maxWidth: "100%" }}
        >
          <Tab
            value="lives"
            label={intl.formatMessage({ id: "liveStreams.tabs.lives" })}
          />
          <Tooltip
            title={
              youtubeNotConnected
                ? intl.formatMessage({ id: "youtubeConnection.blockingWarning" })
                : ""
            }
            {...({ value: "playlists" } as any)}
          >
            <span>
              {/* MUI's Tabs only wires up value/selected/onChange on its *direct*
                  children — wrapping Tab in Tooltip+span for the tooltip breaks that.
                  `value` on the Tooltip lets Tabs position the active-tab indicator
                  correctly; `selected`/`onChange` below make the click itself work. */}
              <Tab
                value="playlists"
                label={intl.formatMessage({ id: "liveStreams.tabs.playlists" })}
                disabled={youtubeNotConnected}
                {...({
                  selected: tab === "playlists",
                  onChange: (_: unknown, v: unknown) => handleTabChange(v),
                } as any)}
              />
            </span>
          </Tooltip>
          {canOperate ? (
            <Tab
              value="media"
              label={intl.formatMessage({ id: "liveStreams.tabs.media" })}
            />
          ) : null}
        </Tabs>
        <Tooltip
          title={
            youtubeConnectionBlocked
              ? intl.formatMessage({ id: "youtubeConnection.blockingWarning" })
              : ""
          }
        >
          <span style={{ width: "100%" }}>
            <LoadingButton
              variant="contained"
              size="small"
              fullWidth={false}
              startIcon={<AddRoundedIcon />}
              onClick={() => setScheduleDialogOpen(true)}
              disabled={youtubeConnectionBlocked}
              sx={{ mb: 1, width: { xs: "100%", sm: "auto" } }}
            >
              {intl.formatMessage({ id: "liveStreams.newLive" })}
            </LoadingButton>
          </span>
        </Tooltip>
        {isAdmin ? (
          <LoadingButton
            variant="text"
            size="small"
            fullWidth={false}
            onClick={() => setBillingConfigOpen(true)}
            sx={{ mb: 1, width: { xs: "100%", sm: "auto" } }}
          >
            {intl.formatMessage({ id: "liveStreams.period.billingConfig.title" })}
          </LoadingButton>
        ) : null}
      </Stack>

      <LiveCreationDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        canOperate={canOperate}
        isAdmin={isAdmin}
        isOwnerOnly={isOwnerOnly}
        isLocalhost={isLocalhost}
        establishments={establishments}
        setEstablishments={setEstablishments}
        establishmentsReady={establishmentsReady}
        selectedEstablishmentId={selectedEstablishmentId}
        setSelectedEstablishmentId={setSelectedEstablishmentId}
        selectedEstablishment={selectedEstablishment}
        cameraCourts={cameraCourts}
        templatesLoading={templatesLoading}
        livePlaylists={livePlaylists}
        setLivePlaylists={setLivePlaylists}
        playlistListContextLiveId={playlistListContextLiveId}
        youtubeConnectionStatus={youtubeConnectionStatus}
        youtubeConnectionBlocked={youtubeConnectionBlocked}
        onCreated={() => setFocusLiveId(null)}
      />
      <BillingConfigPanel
        open={billingConfigOpen}
        onClose={() => setBillingConfigOpen(false)}
      />

      {tab === "lives" ? (
        <Fade in timeout={200}>
          <div>
            <LivesManager
              establishmentName={
                !canOperate ? selectedEstablishment?.name : undefined
              }
              focusLiveId={focusLiveId}
            />
          </div>
        </Fade>
      ) : null}
      {tab === "playlists" ? (
        <Fade in timeout={200}>
          <div>
            <PlaylistsManager youtubeBlocked={youtubeNotConnected} />
          </div>
        </Fade>
      ) : null}
      {canOperate && tab === "media" ? (
        <Fade in timeout={200}>
          <div>
            <MediaManager />
          </div>
        </Fade>
      ) : null}

      {!canOperate ? (
        <Dialog
          open={youtubeDisconnectConfirmOpen}
          onClose={() => {
            if (!youtubeConnectionLoading)
              setYoutubeDisconnectConfirmOpen(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {intl.formatMessage({
              id: "youtubeConnection.disconnectConfirmTitle",
            })}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {intl.formatMessage({
                id: "youtubeConnection.disconnectConfirmBody",
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              variant="text"
              size="small"
              color="inherit"
              fullWidth={false}
              onClick={() => setYoutubeDisconnectConfirmOpen(false)}
              disabled={youtubeConnectionLoading}
            >
              {intl.formatMessage({ id: "global.cancel" })}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              fullWidth={false}
              size="small"
              color="error"
              disabled={youtubeConnectionLoading}
              onClick={() => {
                void handleDisconnectYoutube();
              }}
            >
              {intl.formatMessage({ id: "youtubeConnection.disconnect" })}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
}
